import type { FaqAdminItem, FaqInput } from '@kk/domain';
import { Card, Text, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { useClients, useContentCan } from '@/auth/SessionProvider';
import { Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useErrorMessage, useFaq, useFaqMutation } from '@/data/hooks';

const c = deck.help;

/** Paragraphs as the editor shows them: a blank line between each. */
export const answerToText = (answer: string[]) => answer.join('\n\n');
export const textToAnswer = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

/**
 * **HELP: THE FAQ** (P23's Help row, 3.0.10): every question, drafts and live, in display order. Writers draft and
 * edit; Publishers put a question in the app and take it out; a live question is taken out before it can be deleted.
 */
export function HelpScreen() {
  const faq = useFaq();
  const errorMessage = useErrorMessage();
  const mayEdit = useContentCan('edit');
  const [editing, setEditing] = useState<{ id: string | null; form: FaqInput } | null>(null);
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
        {mayEdit && !editing ? (
          <WasabiButton
            label={c.newQuestion}
            size="sm"
            fullWidth={false}
            onPress={() => setEditing({ id: null, form: { question: '', answer: [] } })}
            testID="faq-new"
          />
        ) : null}
      </div>
      {editing ? <Editor editing={editing} onDone={() => setEditing(null)} /> : null}
      {faq.error ? <Status testID="faq-error">{errorMessage(faq.error)}</Status> : null}
      <div className="stack">
        {(faq.data ?? []).map((item) => (
          <Item
            key={item.id}
            item={item}
            onEdit={
              mayEdit
                ? () =>
                    setEditing({
                      id: item.id,
                      form: { question: item.question, answer: item.answer, order: item.order },
                    })
                : undefined
            }
          />
        ))}
      </div>
    </>
  );
}

function Editor({
  editing,
  onDone,
}: {
  editing: { id: string | null; form: FaqInput };
  onDone: () => void;
}) {
  const { data } = useClients();
  const errorMessage = useErrorMessage();
  const [question, setQuestion] = useState(editing.form.question);
  const [answer, setAnswer] = useState(answerToText(editing.form.answer));
  const [order, setOrder] = useState(
    editing.form.order !== undefined ? String(editing.form.order) : '',
  );
  const save = useFaqMutation((input: FaqInput) =>
    editing.id ? data.updateFaqItem(editing.id, input) : data.createFaqItem(input),
  );
  return (
    <Card>
      <form
        className="stack"
        data-testid="faq-editor"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(
            {
              question,
              answer: textToAnswer(answer),
              ...(order.trim() ? { order: Number(order) } : {}),
            },
            { onSuccess: onDone },
          );
        }}
      >
        <label className="stack">
          <span className="field-label">{c.fields.question}</span>
          <input
            className="glyph-search"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            data-testid="faq-field-question"
          />
        </label>
        <label className="stack">
          <span className="field-label">{c.fields.answer}</span>
          <textarea
            className="script-text"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            data-testid="faq-field-answer"
          />
        </label>
        <label className="stack">
          <span className="field-label">{c.fields.order}</span>
          <input
            className="glyph-search"
            type="number"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            data-testid="faq-field-order"
          />
        </label>
        {save.error ? <Status testID="faq-save-error">{errorMessage(save.error)}</Status> : null}
        <div className="actions">
          <button type="submit" className="chip on" data-testid="faq-save">
            {c.save}
          </button>
          <button type="button" className="chip" onClick={onDone}>
            {c.cancel}
          </button>
        </div>
      </form>
    </Card>
  );
}

function Item({ item, onEdit }: { item: FaqAdminItem; onEdit?: () => void }) {
  const { data } = useClients();
  const errorMessage = useErrorMessage();
  const mayPublish = useContentCan('publish');
  const act = useFaqMutation(async (a: 'publish' | 'unpublish' | 'delete'): Promise<void> => {
    if (a === 'delete') await data.deleteFaqItem(item.id);
    else await data.faqItemAction(item.id, a);
  });
  return (
    <Card>
      <div className="stack" data-testid={`faq-item-${item.id}`}>
        <Text variant="bodySm" tone="muted">
          {c.status[item.status]}
        </Text>
        <Text variant="body" tone="ink">
          {item.question}
        </Text>
        {item.answer.map((p, i) => (
          <Text key={i} variant="bodySm" tone="ink">
            {p}
          </Text>
        ))}
        <div className="actions">
          {onEdit ? (
            <button
              type="button"
              className="chip"
              onClick={onEdit}
              data-testid={`faq-edit-${item.id}`}
            >
              {c.edit}
            </button>
          ) : null}
          {mayPublish && item.status === 'draft' ? (
            <button
              type="button"
              className="chip on"
              onClick={() => act.mutate('publish')}
              data-testid={`faq-publish-${item.id}`}
            >
              {c.publish}
            </button>
          ) : null}
          {mayPublish && item.status === 'live' ? (
            <button
              type="button"
              className="chip"
              onClick={() => act.mutate('unpublish')}
              data-testid={`faq-unpublish-${item.id}`}
            >
              {c.unpublish}
            </button>
          ) : null}
          {onEdit && item.status === 'draft' ? (
            <button
              type="button"
              className="chip"
              onClick={() => act.mutate('delete')}
              data-testid={`faq-delete-${item.id}`}
            >
              {c.delete}
            </button>
          ) : null}
        </div>
        {act.error ? (
          <Status testID={`faq-act-error-${item.id}`}>{errorMessage(act.error)}</Status>
        ) : null}
      </div>
    </Card>
  );
}
